import React, { useEffect } from "react";
import {
  Checkbox,
  Alert,
  AlertDescription,
  DismissibleTag,
} from "@jem-open/jem-ui";
import AffiliatedCompany from "../services/model/AffiliatedCompany";
import McmSegment from "../services/model/McmSegment";
import Segment from "../../segments/services/model/Segment";

type CompanySegmentPickerProps = {
  companies: AffiliatedCompany[];
  segments: McmSegment[];
  ownerCompanyId?: number;
  ownerCompanyName?: string;
  ownerSegments?: Segment[];
  selectedCompanyIds: number[];
  selectedSegmentIds: number[];
  selectedOwnerSegmentIds: number[];
  estimatedAudience: { total: number; per_employer: Record<string, number> } | null;
  isEstimating: boolean;
  onSelectedCompanyIdsChange: (ids: number[]) => void;
  onSelectedSegmentIdsChange: (ids: number[]) => void;
  onSelectedOwnerSegmentIdsChange: (ids: number[]) => void;
};

const CompanySegmentPicker: React.FC<CompanySegmentPickerProps> = ({
  companies,
  segments,
  ownerCompanyId,
  ownerCompanyName,
  ownerSegments,
  selectedCompanyIds,
  selectedSegmentIds,
  selectedOwnerSegmentIds,
  estimatedAudience,
  isEstimating,
  onSelectedCompanyIdsChange,
  onSelectedSegmentIdsChange,
  onSelectedOwnerSegmentIdsChange,
}) => {
  const toggleCompany = (companyId: number): void => {
    if (selectedCompanyIds.includes(companyId)) {
      onSelectedCompanyIdsChange(selectedCompanyIds.filter((id) => id !== companyId));
    } else {
      onSelectedCompanyIdsChange([...selectedCompanyIds, companyId]);
    }
  };

  const isOwnerSelected = ownerCompanyId != null && selectedCompanyIds.includes(ownerCompanyId);
  const hasAffiliatedCompanies = selectedCompanyIds.some((id) => id !== ownerCompanyId);

  const toggleOwnerCompany = (): void => {
    if (ownerCompanyId == null) return;
    if (isOwnerSelected) {
      onSelectedCompanyIdsChange(selectedCompanyIds.filter((id) => id !== ownerCompanyId));
    } else {
      onSelectedCompanyIdsChange([...selectedCompanyIds, ownerCompanyId]);
    }
  };

  const toggleSegment = (segmentId: number): void => {
    if (selectedSegmentIds.includes(segmentId)) {
      onSelectedSegmentIdsChange(selectedSegmentIds.filter((id) => id !== segmentId));
    } else {
      onSelectedSegmentIdsChange([...selectedSegmentIds, segmentId]);
    }
  };

  const toggleOwnerSegment = (segmentId: number): void => {
    if (selectedOwnerSegmentIds.includes(segmentId)) {
      onSelectedOwnerSegmentIdsChange(selectedOwnerSegmentIds.filter((id) => id !== segmentId));
    } else {
      onSelectedOwnerSegmentIdsChange([...selectedOwnerSegmentIds, segmentId]);
    }
  };

  // Only show MCM segments belonging to the selected affiliated companies.
  // Exclude segments targeting the owner — owner targeting is via owner segments only.
  const filteredSegments = segments.filter(
    (seg) =>
      seg.target_company &&
      selectedCompanyIds.includes(seg.target_company.id) &&
      seg.target_company.id !== ownerCompanyId,
  );

  // Owner segments visible when the owner company is selected.
  // Exclude MCM segments — the /segments/ API returns all segments for the employer
  // including MCM-configured ones. Only non-MCM segments are valid as owner segments.
  const mcmSegmentIds = new Set(segments.map((s) => s.id));
  const filteredOwnerSegments = isOwnerSelected
    ? (ownerSegments || []).filter((s) => !mcmSegmentIds.has(s.id))
    : [];

  // When companies are deselected, remove any orphaned segment selections.
  // Intentionally keyed only on selectedCompanyIds — runs cleanup when company selection changes.
  useEffect(() => {
    const currentFilteredSegments = segments.filter(
      (seg) =>
        seg.target_company &&
        selectedCompanyIds.includes(seg.target_company.id) &&
        seg.target_company.id !== ownerCompanyId,
    );
    const validSegmentIds = currentFilteredSegments.map((s) => s.id);
    const cleaned = selectedSegmentIds.filter((id) => validSegmentIds.includes(id));
    if (cleaned.length !== selectedSegmentIds.length) {
      onSelectedSegmentIdsChange(cleaned);
    }
  }, [selectedCompanyIds, segments, selectedSegmentIds, onSelectedSegmentIdsChange, ownerCompanyId]);

  // When owner company is deselected, clear orphaned owner segment selections.
  useEffect(() => {
    if (!isOwnerSelected && selectedOwnerSegmentIds.length > 0) {
      onSelectedOwnerSegmentIdsChange([]);
    }
  }, [isOwnerSelected, selectedOwnerSegmentIds.length, onSelectedOwnerSegmentIdsChange]);

  return (
    <div className="flex flex-col gap-xs">
      {/* Companies section */}
      <div className="flex flex-col gap-xxs">
        <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
          Select companies
        </p>
        <p className="text-sm font-regular leading-18 text-greyscale-text-body">
          Choose which companies should receive this announcement.
        </p>
      </div>

      <div className="border border-greyscale-border rounded-lg overflow-hidden">
        {/* Owner company — first entry */}
        {ownerCompanyId != null && ownerCompanyName && (
          <div
            role="button"
            tabIndex={0}
            className="flex items-center justify-between px-md py-xs hover:bg-greyscale-bg-subtle/50 cursor-pointer border-b border-greyscale-border"
            onClick={toggleOwnerCompany}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleOwnerCompany();
              }
            }}
          >
            <div className="flex items-center gap-xxs">
              <Checkbox
                checked={isOwnerSelected}
                onCheckedChange={() => undefined}
                aria-label={`Select ${ownerCompanyName}`}
              />
              <span className="text-sm text-greyscale-text-title">
                {ownerCompanyName}
              </span>
              <span className="text-xs text-greyscale-text-caption">(Owner)</span>
            </div>
            <span className="text-sm text-greyscale-text-caption">
              &mdash;
            </span>
          </div>
        )}

        {/* Affiliated companies */}
        {companies.map((company) => {
          const employerId = company.target_employer.id;
          const isSelected = selectedCompanyIds.includes(employerId);
          return (
            <div
              key={company.id}
              role="button"
              tabIndex={0}
              className="flex items-center justify-between px-md py-xs hover:bg-greyscale-bg-subtle/50 cursor-pointer border-b last:border-b-0 border-greyscale-border"
              onClick={() => toggleCompany(employerId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleCompany(employerId);
                }
              }}
            >
              <div className="flex items-center gap-xxs">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => undefined}
                  aria-label={`Select ${company.target_employer.company_name}`}
                />
                <span className="text-sm text-greyscale-text-title">
                  {company.target_employer.company_name}
                </span>
              </div>
              <span className="text-sm text-greyscale-text-caption">
                {company.employee_count} employees
              </span>
            </div>
          );
        })}
      </div>

      {/* Hint when only owner company is selected */}
      {isOwnerSelected && !hasAffiliatedCompanies && (
        <p className="text-xs text-red-500 mt-xxxs">
          At least one affiliated company is required to send an announcement.
        </p>
      )}

      {/* Hint when owner is selected but no owner segments chosen */}
      {isOwnerSelected && hasAffiliatedCompanies && selectedOwnerSegmentIds.length === 0 && (
        <p className="text-xs text-amber-600 mt-xxxs">
          Select at least one segment to target your own employees.
        </p>
      )}

      {/* Segments section — only visible when at least one company is selected */}
      {selectedCompanyIds.length > 0 && (
      <>
      <div className="flex flex-col gap-xxs mt-sm">
        <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
          Select segments (optional)
        </p>
        <p className="text-sm font-regular leading-18 text-greyscale-text-body">
          Narrow the audience by selecting specific segments, or leave empty to target all employees.
        </p>
      </div>

      {(filteredOwnerSegments.length > 0 || filteredSegments.length > 0) ? (
        <div className="border border-greyscale-border rounded-lg overflow-hidden">
          {/* Owner segments */}
          {filteredOwnerSegments.map((seg) => {
            const isSelected = selectedOwnerSegmentIds.includes(seg.id);
            return (
              <div
                key={`owner-seg-${seg.id}`}
                role="button"
                tabIndex={0}
                className="flex items-center justify-between px-md py-xs hover:bg-greyscale-bg-subtle/50 cursor-pointer border-b last:border-b-0 border-greyscale-border"
                onClick={() => toggleOwnerSegment(seg.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleOwnerSegment(seg.id);
                  }
                }}
              >
                <div className="flex items-center gap-xxs">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => undefined}
                    aria-label={`Select ${seg.name}`}
                  />
                  <span className="text-sm text-greyscale-text-body">
                    {seg.name}
                  </span>
                  <span className="text-xs text-greyscale-text-caption">(Owner)</span>
                </div>
                <span className="text-sm text-greyscale-text-caption">
                  {seg.matched_employee_count ?? "\u2014"} employees
                </span>
              </div>
            );
          })}

          {/* MCM segments */}
          {filteredSegments.map((seg) => {
            const isSelected = selectedSegmentIds.includes(seg.id);
            return (
              <div
                key={seg.id}
                role="button"
                tabIndex={0}
                className="flex items-center justify-between px-md py-xs hover:bg-greyscale-bg-subtle/50 cursor-pointer border-b last:border-b-0 border-greyscale-border"
                onClick={() => toggleSegment(seg.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSegment(seg.id);
                  }
                }}
              >
                <div className="flex items-center gap-xxs">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => undefined}
                    aria-label={`Select ${seg.name}`}
                  />
                  <span className="text-sm text-greyscale-text-body">
                    {seg.name}
                  </span>
                </div>
                <span className="text-sm text-greyscale-text-caption">
                  {seg.matched_employee_count ?? "\u2014"} employees
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-greyscale-text-caption">No segments available for the selected companies.</p>
      )}
      </>
      )}

      {/* Selected tags */}
      {(selectedCompanyIds.length > 0 || selectedSegmentIds.length > 0 || selectedOwnerSegmentIds.length > 0) && (
        <div className="flex flex-wrap gap-xxs mt-xxxs">
          {/* Owner company tag */}
          {isOwnerSelected && ownerCompanyName && (
            <DismissibleTag
              key={`c-owner-${ownerCompanyId}`}
              variant="pink"
              onDismiss={toggleOwnerCompany}
            >
              {ownerCompanyName}
            </DismissibleTag>
          )}

          {/* Affiliated company tags */}
          {selectedCompanyIds
            .filter((empId) => empId !== ownerCompanyId)
            .map((empId) => {
              const comp = companies.find((c) => c.target_employer.id === empId);
              if (!comp) return null;
              return (
                <DismissibleTag
                  key={`c-${empId}`}
                  variant="pink"
                  onDismiss={() => toggleCompany(empId)}
                >
                  {comp.target_employer.company_name}
                </DismissibleTag>
              );
            })}

          {/* Owner segment tags */}
          {selectedOwnerSegmentIds.map((segId) => {
            const seg = (ownerSegments || []).find((s) => s.id === segId);
            if (!seg) return null;
            return (
              <DismissibleTag
                key={`os-${segId}`}
                variant="navy"
                onDismiss={() => toggleOwnerSegment(segId)}
              >
                {seg.name}
              </DismissibleTag>
            );
          })}

          {/* MCM segment tags */}
          {selectedSegmentIds.map((segId) => {
            const seg = filteredSegments.find((s) => s.id === segId);
            if (!seg) return null;
            return (
              <DismissibleTag
                key={`s-${segId}`}
                variant="navy"
                onDismiss={() => toggleSegment(segId)}
              >
                {seg.name}
              </DismissibleTag>
            );
          })}
        </div>
      )}

      {/* Scope summary */}
      {selectedCompanyIds.length > 0 && (
        <Alert variant="warning" className="mt-xxxs">
          <AlertDescription>
            {isEstimating && "Estimating audience..."}
            {!isEstimating && estimatedAudience && (
              <>
                This message will be sent to{" "}
                <strong>{estimatedAudience.total} employees</strong> across{" "}
                <strong>{selectedCompanyIds.length} companies</strong>
                {(selectedSegmentIds.length + selectedOwnerSegmentIds.length) > 0 && (
                  <>
                    {" "}in{" "}
                    <strong>
                      {selectedSegmentIds.length + selectedOwnerSegmentIds.length}{" "}
                      {(selectedSegmentIds.length + selectedOwnerSegmentIds.length) === 1 ? "segment" : "segments"}
                    </strong>
                  </>
                )}
                .
              </>
            )}
            {!isEstimating && !estimatedAudience && (
              <>
                <strong>{selectedCompanyIds.length} companies</strong> selected
                {(selectedSegmentIds.length + selectedOwnerSegmentIds.length) > 0 && (
                  <>
                    , <strong>{selectedSegmentIds.length + selectedOwnerSegmentIds.length} segments</strong>
                  </>
                )}
                . All employees will be targeted.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CompanySegmentPicker;
